import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { SheldonItem, SpecialClass } from '@api/fetchers/prime/types';
import { segment } from '@actions/analytics';
import {
  PRIME_TAB_VIEW_ALL_CLICKED,
  USER_FREE_QUOTA_REACHED
} from '@constants/segment';
import { getPrimeGlobalProps } from '@stores/prime-quota/selectors';
import usePrimeTrialPromoModalStore from '@stores/prime-pitch/store';
import { PrimeTrialPromoContextualId } from '@stores/prime-pitch/types';
import usePrimeHomeStore from '@stores/prime-home';
import { getPrimeVideoSlug, getSlug } from '@utils/prime';

interface UsePrimeHomeHandlersProps {
  selectedSubCategory: string;
  selectedCategoryUID: string;
  selectedSubCategoryMeta: any;
  sortedSubCategories: any[];
  selectedGoal: any;
  goalUID: string;
  goalSlug: string;
  isQuotaExhausted: boolean;
  unlockedVideosUids: string[];
}

const usePrimeHomeHandlers = ({
  selectedSubCategory,
  selectedCategoryUID,
  selectedSubCategoryMeta,
  sortedSubCategories,
  selectedGoal,
  goalUID,
  goalSlug,
  isQuotaExhausted,
  unlockedVideosUids
}: UsePrimeHomeHandlersProps) => {
  const router = useRouter();
  const openPrimeTrialPromoModal = usePrimeTrialPromoModalStore(
    (state) => state.openModal
  );
  const setPrimeBreadcrumbs = usePrimeHomeStore(
    (state) => state.setPrimeBreadcrumbs
  );

  const fireVideoQuotaReached = useCallback(() => {
    segment.track(USER_FREE_QUOTA_REACHED, {
      duration_limit: 'Daily',
      limit_type: 'Videos',
      goal_uid: goalUID || 'NA',
      goal_name: selectedGoal?.name || 'NA',
      ...getPrimeGlobalProps()
    });
  }, [goalUID, selectedGoal?.name]);

  const trackViewAllClicked = useCallback(
    ({
      playlistName,
      playlistUid,
      playlistRank,
      playlistNestingType,
      playlistType,
      lastPrimarySourceSection
    }: {
      playlistName?: string;
      playlistUid?: string;
      playlistRank?: number;
      playlistNestingType?: string;
      playlistType?: string;
      lastPrimarySourceSection?: string;
    }) => {
      const sanitized = (value?: string | number | null, fallback = 'NA') => {
        if (value === null || value === undefined || value === '') {
          return fallback;
        }
        return String(value);
      };

      const subCategoryRank = sortedSubCategories.findIndex(
        (category) => category.uid === selectedSubCategory
      );

      segment.track(PRIME_TAB_VIEW_ALL_CLICKED, {
        video_category_name: sanitized(selectedGoal?.name),
        video_category_uid: sanitized(goalUID),
        video_sub_category_name: sanitized(selectedSubCategoryMeta?.name),
        video_sub_category_uid: sanitized(selectedSubCategory),
        video_sub_category_rank:
          subCategoryRank >= 0 ? subCategoryRank + 1 : 'NA',
        playlist_name: sanitized(playlistName),
        playlist_uid: sanitized(playlistUid),
        playlist_rank: playlistRank,
        playlist_nesting_type: sanitized(playlistNestingType),
        playlist_type: sanitized(playlistType),
        last_primary_source: sanitized(undefined, 'Primetab'),
        last_primary_souce_section: sanitized(
          lastPrimarySourceSection,
          'Playlist'
        ),
        path: sanitized('Prime Tab', 'Prime Tab'),
        goal_uid: sanitized(goalUID),
        goal_name: sanitized(selectedGoal?.name),
        ...getPrimeGlobalProps()
      });
    },
    [
      sortedSubCategories,
      selectedSubCategory,
      selectedGoal?.name,
      goalUID,
      selectedSubCategoryMeta?.name
    ]
  );

  const openPrimeVideo = useCallback(
    (sectionName: string, videoUid: string, playlistUid: string) => {
      if (!videoUid) return;
      router.push(
        `/prime/${selectedSubCategory}/${getPrimeVideoSlug(
          sectionName,
          videoUid
        )}/${videoUid}?playlistUid=${playlistUid}`
      );
    },
    [router, selectedSubCategory]
  );

  const handleCardClick = useCallback(
    (
      item: SheldonItem | SpecialClass,
      sectionName: string,
      sectionUid: string,
      sectionRank?: number,
      playlistName?: string
    ) => {
      setPrimeBreadcrumbs(
        [
          {
            type: 'section',
            title: sectionName,
            rank: sectionRank ?? null
          },
          playlistName
            ? {
                type: 'playlist',
                title: playlistName
              }
            : null
        ].filter(Boolean)
      );

      if ('contentType' in item && item.contentType === 'playlist') {
        router.push(
          `/prime/${selectedSubCategory}/${getSlug(item.value.name)}/${
            item.value.uid
          }/all`
        );
        return;
      }
      if ('contentType' in item) {
        const isLoggedOut = !goalUID;
        const isVideoLocked =
          !isLoggedOut &&
          isQuotaExhausted &&
          item.value.uid &&
          !unlockedVideosUids.includes(item.value.uid);
        if (isVideoLocked) {
          fireVideoQuotaReached();
          openPrimeTrialPromoModal(
            PrimeTrialPromoContextualId.VIDEO,
            goalUID,
            goalSlug,
            {
              dismissNavigateTo: null,
              analyticsContext: { contentVideoUid: item.value.uid }
            }
          );
          return;
        }
        openPrimeVideo(sectionName, item.value.uid, sectionUid);
        return;
      }
      const isLoggedOut = !goalUID;
      const isLocked =
        !isLoggedOut &&
        isQuotaExhausted &&
        item.uid &&
        !unlockedVideosUids.includes(item.nextSession?.properties?.uid);
      if (isLocked) {
        fireVideoQuotaReached();
        openPrimeTrialPromoModal(
          PrimeTrialPromoContextualId.VIDEO,
          goalUID,
          goalSlug,
          {
            dismissNavigateTo: null,
            analyticsContext: {
              contentVideoUid: item.nextSession?.properties?.uid
            }
          }
        );
        return;
      }
      const relativeLink = item.nextSession?.properties?.relativeLink;
      if (relativeLink) {
        router.push(relativeLink);
      }
    },
    [
      setPrimeBreadcrumbs,
      router,
      selectedSubCategory,
      goalUID,
      isQuotaExhausted,
      unlockedVideosUids,
      fireVideoQuotaReached,
      openPrimeTrialPromoModal,
      goalSlug,
      openPrimeVideo
    ]
  );

  const setPrimeSectionBreadcrumb = useCallback(
    (sectionName: string, sectionRank?: number) => {
      setPrimeBreadcrumbs([
        {
          type: 'section',
          title: sectionName,
          rank: sectionRank ?? null
        }
      ]);
    },
    [setPrimeBreadcrumbs]
  );

  const handleSearchClick = useCallback(() => {
    router.push(
      `/goal/${goalSlug}/${goalUID}/prime/search/${selectedCategoryUID}`
    );
  }, [router, goalSlug, goalUID, selectedCategoryUID]);

  return {
    handleCardClick,
    openPrimeVideo,
    trackViewAllClicked,
    setPrimeSectionBreadcrumb,
    handleSearchClick
  };
};

export default usePrimeHomeHandlers;
